// ============================================================
// collision.js
//
// Real-geometry collision for the vehicle approach simulation (and, later,
// equipment overlap warnings). Replaces the old "vehicle = a fat point,
// equipment = its axis-aligned bounding box, keep ~0.8 × half a car width
// away from everything" model, which made the sim far stricter than a real
// shop floor.
//
// The model now:
//   - The vehicle is an oriented rectangle (its real length × width),
//     centered on the path pose and turned to its heading.
//   - Each placed item contributes only its SOLID parts, as oriented
//     rectangles in its own rotation (not a bounding box):
//       • 2-post lift → its two posts (arms are swung clear to drive in)
//       • 4-post lift → four posts + runways + end crossbeams; when it's
//         the lift being driven ONTO, just the posts (you drive up the runways)
//       • scissor / in-ground lifts → nothing when lowered (drive-over)
//       • rooms → their outline (rect) or their edges (free-form)
//       • everything else → its real footprint
//   - Shop walls are thin segments, with every overhead door opening cut
//     out, so the vehicle threads the actual doorway (jambs included).
//   - Required clearance to anything is CLEARANCE_FT = 1 inch — the same
//     margin a careful driver works to in a real bay.
//
// Everything is in shop feet. Item rotation follows the app convention
// used across cadUtils/vehiclePath: local x = width (fw), local y = depth
// (fh), rotated by item.rot degrees with x' = x·cos − y·sin.
// ============================================================

import { pointInPolygon } from './shopGeometry';

// 1 inch, in feet — the clearance demanded between the vehicle body and
// anything solid (equipment, posts, walls, door jambs)
export const CLEARANCE_FT = 1 / 12;

// Real-ish lift part sizes (feet). Per-model geometry can replace these
// later; they're deliberately close to common Rotary/Forward/BendPak specs.
const POST_W = 1.5;          // 2-/4-post column footprint (square)
const RUNWAY_W = 20 / 12;    // 4-post runway width (≈20")
const RUNWAY_GAP = 40 / 12;  // clear width between 4-post runways (≈40")
const CROSSBEAM_D = 1;       // 4-post end crossbeam depth

const deg2rad = (d) => (d * Math.PI) / 180;

// ---------- Oriented box helpers ----------

// An oriented box: center (cx, cy), half-extents along its own axes
// (hx, hy), and its angle in radians. cos/sin and the bounding-circle
// radius are cached because the path search runs this check a lot.
export const makeBox = (cx, cy, hx, hy, ang) => ({
  cx, cy, hx, hy, ang,
  c: Math.cos(ang), s: Math.sin(ang), r: Math.hypot(hx, hy),
});

// A box placed at local offset (lx, ly) inside an item, sized w × h in the
// item's local axes — converted to world coordinates.
function partBox(item, lx, ly, w, h) {
  const r = deg2rad(item.rot || 0);
  const c = Math.cos(r), s = Math.sin(r);
  return makeBox(item.cx + lx * c - ly * s, item.cy + lx * s + ly * c, w / 2, h / 2, r);
}

// A wall segment as a zero-thickness box (inflating it by the clearance
// later gives it a 2-inch-thick "no closer than 1 inch" band)
export function segmentBox(a, b) {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  return makeBox((a.x + b.x) / 2, (a.y + b.y) / 2, len / 2, 0, Math.atan2(b.y - a.y, b.x - a.x));
}

// Separating Axis Test for two oriented boxes. `clear` grows box B on
// every side, so "overlap" means "closer than `clear`".
export function boxesOverlap(A, B, clear = 0) {
  const dx = B.cx - A.cx, dy = B.cy - A.cy;
  // Quick reject: farther apart than their bounding circles can reach.
  // Most checks end here, which keeps the path search fast.
  const reach = A.r + B.r + clear * 1.4143;
  if (dx * dx + dy * dy > reach * reach) return false;
  const bhx = B.hx + clear, bhy = B.hy + clear;
  // The 4 candidate separating axes: each box's two edge directions
  return !(
    separated(A.c, A.s, A, B, bhx, bhy, dx, dy) ||
    separated(-A.s, A.c, A, B, bhx, bhy, dx, dy) ||
    separated(B.c, B.s, A, B, bhx, bhy, dx, dy) ||
    separated(-B.s, B.c, A, B, bhx, bhy, dx, dy)
  );
}

// True if the boxes' shadows on axis (ax, ay) don't overlap
function separated(ax, ay, A, B, bhx, bhy, dx, dy) {
  const ra = A.hx * Math.abs(A.c * ax + A.s * ay) + A.hy * Math.abs(-A.s * ax + A.c * ay);
  const rb = bhx * Math.abs(B.c * ax + B.s * ay) + bhy * Math.abs(-B.s * ax + B.c * ay);
  return Math.abs(dx * ax + dy * ay) > ra + rb;
}

// ---------- What's solid about each item ----------

// Returns the solid boxes for one placed item. `isTarget` = this is the
// lift the vehicle is parking on (its drive-on surfaces don't count).
export function itemSolids(item, { isTarget = false } = {}) {
  const W = (item.fw || 0) * (item.scaleX || 1);
  const D = (item.fh || 0) * (item.scaleY || 1);
  const hw = W / 2, hd = D / 2;

  switch (item.id) {
    case '2post':
      // Two columns centered fore-aft at the left/right edges of the footprint.
      // Arms are low and swung out of the way to drive in, so only posts count.
      return [
        partBox(item, -hw + POST_W / 2, 0, POST_W, POST_W),
        partBox(item, hw - POST_W / 2, 0, POST_W, POST_W),
      ];

    case '4post': {
      const posts = [
        [-hw + POST_W / 2, -hd + POST_W / 2], [hw - POST_W / 2, -hd + POST_W / 2],
        [-hw + POST_W / 2, hd - POST_W / 2], [hw - POST_W / 2, hd - POST_W / 2],
      ].map(([x, y]) => partBox(item, x, y, POST_W, POST_W));
      if (isTarget) return posts;   // driving up onto its runways is the point
      // Runways (≈20" wide, ≈40" apart, centered) between the crossbeams,
      // plus the two end crossbeams spanning post to post
      const runX = RUNWAY_GAP / 2 + RUNWAY_W / 2;
      const runLen = Math.max(0, D - 2 * CROSSBEAM_D);
      return [
        ...posts,
        partBox(item, -runX, 0, RUNWAY_W, runLen),
        partBox(item, runX, 0, RUNWAY_W, runLen),
        partBox(item, 0, -hd + CROSSBEAM_D / 2, W, CROSSBEAM_D),
        partBox(item, 0, hd - CROSSBEAM_D / 2, W, CROSSBEAM_D),
      ];
    }

    case 'scissor':
    case 'inground':
      // Low-rise scissor platforms and in-ground lifts are driven over
      // when lowered — nothing to hit
      return [];

    default:
      // Rooms: free-form ones block along their edges, rectangular ones
      // (and every other piece of equipment) block their real footprint
      if (item.isRoom && item.shapeType === 'free' && Array.isArray(item.vertices) && item.vertices.length >= 3) {
        const v = item.vertices;
        return v.map((p, i) => segmentBox(p, v[(i + 1) % v.length]));
      }
      if (W <= 0 || D <= 0) return [];
      return [partBox(item, 0, 0, W, D)];
  }
}

// ---------- Shop walls with door openings ----------

// The shop outline as wall segments, with every OVERHEAD door's opening
// removed (walk doors stay solid — no vehicle fits through one). Uses
// the same door geometry as shopGeometry.getDoorEndpoints: `offset` is
// the distance from the wall's start vertex to the door's near edge.
export function wallSegments(shop, doors = []) {
  const verts = shop.vertices || [];
  const walls = shop.walls || [];
  const segs = [];
  walls.forEach((wall) => {
    const a = verts[wall.startVertex], b = verts[wall.endVertex];
    if (!a || !b) return;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len === 0) return;
    const ux = (b.x - a.x) / len, uy = (b.y - a.y) / len;
    // Openings on this wall as [from, to] distances along it, sorted
    const gaps = doors
      .filter((d) => d.wallId === wall.id && d.type === 'overhead')
      .map((d) => {
        const start = Math.max(0, Math.min(len - d.width, d.offset || 0));
        return [start, start + d.width];
      })
      .sort((p, q) => p[0] - q[0]);
    let t = 0;
    const at = (d) => ({ x: a.x + ux * d, y: a.y + uy * d });
    for (const [g0, g1] of gaps) {
      if (g0 > t) segs.push(segmentBox(at(t), at(g0)));
      t = Math.max(t, g1);
    }
    if (t < len) segs.push(segmentBox(at(t), at(len)));
  });
  return segs;
}

// ---------- The vehicle at one pose ----------

export function vehicleBox(pose, vehicle) {
  return makeBox(pose.x, pose.y, vehicle.length / 2, vehicle.width / 2, pose.heading);
}

// Checks one sampled pose. Returns null if clear, else 'space' (wall /
// left the building) or 'obstacle' (equipment).
//   obstacles — solid boxes from itemSolids() for every placed item
//   doorPoint — the active door's midpoint; the vehicle's center is
//               allowed outside the shop while it's still entering
export function checkPose(pose, { vehicle, walls, obstacles, shop, doorPoint }) {
  const box = vehicleBox(pose, vehicle);
  for (const w of walls) {
    if (boxesOverlap(box, w, CLEARANCE_FT)) return 'space';
  }
  // Wall segments catch any crossing, but not a vehicle that slipped out
  // through another door opening — so the center must stay inside the
  // shop, except while it's still threading the active door
  if (shop.vertices && shop.vertices.length >= 3 && !pointInPolygon(pose, shop.vertices)) {
    const nearDoor = doorPoint && Math.hypot(pose.x - doorPoint.x, pose.y - doorPoint.y) <= vehicle.length / 2 + 1;
    if (!nearDoor) return 'space';
  }
  for (const s of obstacles) {
    if (boxesOverlap(box, s, CLEARANCE_FT)) return 'obstacle';
  }
  return null;
}

// ---------- Equipment overlap (placement warnings) ----------

// Solid shapes used to judge whether two PLACED items physically collide.
// Same "real parts only" idea as the drive check, so items can sit right
// up against each other — but here a scissor/in-ground lift counts by its
// whole platform (nothing else can stand on it), and rooms don't count
// at all (equipment belongs inside rooms).
function placementSolids(item) {
  if (item.isRoom) return [];
  if (item.id === 'scissor' || item.id === 'inground') {
    const W = (item.fw || 0) * (item.scaleX || 1), D = (item.fh || 0) * (item.scaleY || 1);
    return W > 0 && D > 0 ? [partBox(item, 0, 0, W, D)] : [];
  }
  return itemSolids(item);
}

// Overlap tolerance: parts must actually push ¼" into each other before it
// counts, so items placed flush (touching) never flag.
const OVERLAP_TOL_FT = 1 / 48;

// Returns { uids: Set of item uids involved, pairs: [[uidA, uidB], ...] }
export function findOverlaps(placed) {
  const parts = placed.map((item) => ({ uid: item.uid, boxes: placementSolids(item) }))
    .filter((p) => p.boxes.length);
  const uids = new Set();
  const pairs = [];
  for (let i = 0; i < parts.length; i++) {
    for (let j = i + 1; j < parts.length; j++) {
      const hit = parts[i].boxes.some((a) => parts[j].boxes.some((b) => boxesOverlap(a, b, -OVERLAP_TOL_FT)));
      if (hit) {
        uids.add(parts[i].uid); uids.add(parts[j].uid);
        pairs.push([parts[i].uid, parts[j].uid]);
      }
    }
  }
  return { uids, pairs };
}
